import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { AIImageModal } from "@/components/magazine/AIImageModal";
import { MagCropModal } from "@/components/magazine/MagCropModal";
import { appPrompt, appConfirm } from "@/components/AppDialogHost";

export default function MagMedia() {
  const [media, setMedia] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const inputRef = useRef(null);
  const load = (query = "") => api.get("/mag/media", { params: { q: query } }).then((r) => setMedia(r.data.media || []));
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    setBusy(true);
    for (const f of Array.from(files || [])) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post("/mag/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (e) { toast.error(e?.response?.data?.detail || `${f.name} failed`); }
    }
    setBusy(false); load(q);
  };
  const editMeta = async (m) => {
    const alt_text = await appPrompt("Alt text:", m.alt_text || "");
    if (alt_text === null) return;
    const caption = await appPrompt("Caption:", m.caption || "");
    await api.put(`/mag/media/${m.media_id}`, { alt_text, caption: caption || "" });
    toast.success("Updated"); load(q);
  };

  return (
    <div className="space-y-4" data-testid="mag-media-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display text-2xl font-bold">Media Library</h1>
        <div className="flex gap-2">
          <button onClick={() => setAiOpen(true)} data-testid="mag-media-page-generate"
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-amber/60 text-brand-amber px-4 py-2 text-sm font-bold">
            <Sparkles className="h-4 w-4" /> Generate with AI</button>
          <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="mag-media-page-upload"
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-amber text-black px-4 py-2 text-sm font-bold">
            <UploadCloud className="h-4 w-4" /> {busy ? "Uploading…" : "Upload Media"}</button>
        </div>
        <input ref={inputRef} type="file" multiple accept="image/*,audio/*,video/mp4" className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
      </div>
      <input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder="Search media…" data-testid="mag-media-search"
        className="rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm min-w-[240px]" />
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {media.map((m) => (
          <div key={m.media_id} className="rounded-lg border border-line overflow-hidden bg-bg-surface" data-testid={`mag-media-card-${m.media_id}`}>
            {m.type === "image" ? <img src={m.file_url} alt={m.alt_text} className="w-full h-28 object-cover" />
              : <div className="w-full h-28 flex items-center justify-center bg-bg-elevated text-xs font-mono text-ink-muted uppercase">{m.type}</div>}
            <div className="p-2 space-y-1">
              <div className="text-[10px] font-mono text-ink-muted truncate">{m.filename}</div>
              <div className="flex gap-1">
                <button onClick={() => editMeta(m)} className="flex-1 text-[10px] rounded border border-line py-0.5">Alt/Caption</button>
                {m.type === "image" && (
                  <button onClick={() => setCropSrc({ src: m.file_url })} data-testid={`mag-media-crop-${m.media_id}`}
                    className="flex-1 text-[10px] rounded border border-line py-0.5 text-brand-cyan">Crop</button>
                )}
                <button onClick={async () => { if (await appConfirm("Delete?")) { await api.delete(`/mag/media/${m.media_id}`); load(q); } }}
                  className="flex-1 text-[10px] rounded border border-line py-0.5 text-red-300/80">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {!media.length && <div className="col-span-6 text-center text-sm text-ink-muted py-10">No media yet.</div>}
      </div>
      <AIImageModal open={aiOpen} onClose={() => setAiOpen(false)} onUse={() => load(q)} />
      <MagCropModal open={!!cropSrc} source={cropSrc} onClose={() => setCropSrc(null)}
        onDone={() => { toast.success("Cropped copy saved to library"); load(q); }} />
    </div>
  );
}
